import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronDown, X } from 'lucide-react';

// options: [{ value, label }] — value is a comma-joined string so it drops into existing query params
export const MultiSelectChips = ({ label, value, options, onChange, testId, width = 'w-40' }) => {
  const selected = value ? value.split(',').filter(Boolean) : [];
  const labelOf = (v) => options.find((o) => o.value === v)?.label || v;
  const toggle = (v) => {
    const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
    onChange(next.join(','));
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`h-8 ${width} px-2 rounded-md border border-stone-200 bg-white text-xs flex items-center gap-1 hover:border-stone-400 transition-colors`}
        >
          {selected.length === 0 && <span className="text-stone-500 truncate">All {label}</span>}
          {selected.length === 1 && <span className="truncate text-stone-900 font-medium">{labelOf(selected[0])}</span>}
          {selected.length > 1 && (
            <span className="truncate text-stone-900 font-medium">
              {label} <span className="ml-0.5 px-1.5 py-px rounded-full bg-stone-900 text-white text-[10px] tabular-nums">{selected.length}</span>
            </span>
          )}
          <ChevronDown className="w-3 h-3 ml-auto text-stone-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}…`} className="h-8 text-xs" />
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1 p-2 border-b border-stone-100" data-testid={`${testId}-chips`}>
              {selected.map((v) => (
                <span key={v} className="inline-flex items-center gap-1 text-[10px] pl-2 pr-1 py-0.5 rounded-full bg-stone-900 text-white">
                  {labelOf(v)}
                  <button type="button" onClick={() => toggle(v)} className="hover:text-rose-300" aria-label={`Remove ${labelOf(v)}`}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              <button type="button" onClick={() => onChange('')} className="text-[10px] text-rose-700 ml-auto hover:underline" data-testid={`${testId}-clear`}>
                Clear
              </button>
            </div>
          )}
          <CommandList className="max-h-64">
            <CommandEmpty className="py-3 text-center text-xs text-stone-400">No matches</CommandEmpty>
            <CommandGroup>
              {options.map((o) => {
                const on = selected.includes(o.value);
                return (
                  <CommandItem key={o.value} value={o.label} onSelect={() => toggle(o.value)} className="text-xs cursor-pointer">
                    <span className={`w-3.5 h-3.5 mr-2 rounded-sm border flex items-center justify-center ${on ? 'bg-stone-900 border-stone-900' : 'border-stone-300'}`}>
                      {on && <Check className="w-2.5 h-2.5 text-white" />}
                    </span>
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
